import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import { RoutingEngine } from '../../utils/routingEngine';
import { TaxEngine, TaxResult } from '../../utils/TaxEngine';
import CalculatorInput from './components/CalculatorInput';
import ResultsPanel from './components/ResultsPanel';
import TaxVisualization from './components/TaxVisualization';
import NearbyTools from './components/NearbyTools';
import FAQSection from './components/FAQSection';
import Breadcrumbs from './components/Breadcrumbs';
import CityBlogTeaser from './components/CityBlogTeaser';
import ForSaleCalculatorBlock from '../../components/feature/ForSaleCalculatorBlock';
import ForSaleFooterSection from '../../components/feature/ForSaleFooterSection';

const CalculatorPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [routingEngine] = useState(() => new RoutingEngine());
  const [locationData, setLocationData] = useState<any>(null);
  const [salary, setSalary] = useState<number>(75000);
  const [frequency, setFrequency] = useState<'annual' | 'monthly' | 'biweekly'>('annual');
  const [filingStatus, setFilingStatus] = useState<string>('single');
  const [taxResult, setTaxResult] = useState<TaxResult | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    const data = routingEngine.parseLocation(location.pathname);
    setLocationData(data);

    if (data) {
      const cityData = data.city || data.state || data.country;
      const taxEngine = new TaxEngine(cityData, data.country);
      const result = taxEngine.calculate(salary, filingStatus);
      setTaxResult(result);
    }
  }, [location.pathname, salary, filingStatus, routingEngine]);

  // Set up document meta tags
  const pageTitle = locationData ? `${locationData.locationName} Salary Tax Calculator 2026 - Take-Home Pay` : 'Salary Tax Calculator';
  const pageDescription = locationData ? `Calculate your exact take-home pay in ${locationData.locationName}. Free salary calculator with ${locationData.locationName} tax rates including federal, state, and local taxes. Updated for 2026.` : 'Calculate your take-home pay with accurate tax rates.';
  const canonicalUrl = `https://mysalarycalculator.net${location.pathname}`;
  const keywords = locationData ? `${locationData.locationName} salary calculator, ${locationData.locationName} tax calculator, ${locationData.locationName} take-home pay, ${locationData.locationName} net salary, ${locationData.locationName} paycheck calculator` : 'salary calculator, tax calculator';

  const structuredData = locationData ? [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": locationData.breadcrumbs.map((crumb: any, index: number) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": `https://mysalarycalculator.net${crumb.path}`
      }))
    },
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": pageTitle,
      "description": pageDescription,
      "url": canonicalUrl,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": locationData.breadcrumbs.map((crumb: any, index: number) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": crumb.name,
          "item": `https://mysalarycalculator.net${crumb.path}`
        }))
      },
      "mainEntity": {
        "@type": "SoftwareApplication",
        "name": `${locationData.locationName} Salary Calculator`,
        "applicationCategory": "FinanceApplication",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": locationData.country.code === 'usa' ? 'USD' : 'GBP'
        }
      }
    }
  ] : [];

  const metaTags = locationData?.city ? [
    { name: 'geo.placename', content: locationData.city.name },
    { name: 'geo.region', content: `${locationData.country.code}-${locationData.state?.code || ''}` }
  ] : [];

  useDocumentMeta({
    title: pageTitle,
    description: pageDescription,
    keywords,
    canonical: canonicalUrl,
    ogTitle: pageTitle,
    ogDescription: pageDescription,
    ogUrl: canonicalUrl,
    ogImage: 'https://public.readdy.ai/ai/img_res/8f3cc483-3168-4437-a291-d29def05c8fe.png',
    twitterCard: 'summary_large_image',
    twitterTitle: pageTitle,
    twitterDescription: pageDescription,
    twitterImage: 'https://public.readdy.ai/ai/img_res/8f3cc483-3168-4437-a291-d29def05c8fe.png',
    metaTags,
    structuredData
  });

  const handleSalaryChange = (newSalary: number) => {
    setSalary(newSalary);
  };

  const getAnnualSalary = () => {
    if (frequency === 'monthly') return salary * 12;
    if (frequency === 'biweekly') return salary * 26;
    return salary;
  };

  const formatCurrency = (amount: number) => {
    return `${locationData?.currencySymbol || '$'}${Math.round(amount).toLocaleString()}`;
  };

  if (!locationData || !taxResult) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading calculator...</p>
        </div>
      </div>
    );
  }

  const annualSalary = getAnnualSalary();

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm" role="banner">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" aria-label="Main navigation">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <a href="/" aria-label="Salary Tax Calculator Home">
                <img 
                  src="https://public.readdy.ai/ai/img_res/8f3cc483-3168-4437-a291-d29def05c8fe.png" 
                  alt="Salary Tax Calculator Logo" 
                  className="h-10 w-auto cursor-pointer"
                  width="40"
                  height="40"
                />
              </a>
              <div className="hidden md:block">
                <span className="text-xl font-bold text-gray-900">Salary Tax Calculator</span>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <a href="/blog" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer hidden md:block">
                <i className="ri-article-line mr-1"></i>Tax Tips Blog
              </a>
              <select 
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:border-gray-400 transition-colors cursor-pointer"
                aria-label="Select language"
              >
                <option>🇺🇸 EN</option>
                <option>🇬🇧 EN-UK</option>
                <option>🇪🇸 ES</option>
                <option>🇫🇷 FR</option>
                <option>🇩🇪 DE</option>
              </select>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-12 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs breadcrumbs={locationData.breadcrumbs} />
          <div className="text-center mt-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <strong>{locationData.locationName} Salary &amp; Tax Calculator 2026</strong>
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Calculate your <strong>take-home pay</strong> with {locationData.locationName}&apos;s exact tax rates. 
              <strong> Free, accurate, and updated for 2026.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <main role="main">
        <section className="py-12" aria-label="Salary calculator">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              <article className="lg:col-span-3">
                <CalculatorInput
                  salary={salary}
                  frequency={frequency}
                  filingStatus={filingStatus}
                  showAdvanced={showAdvanced}
                  currencySymbol={locationData.currencySymbol}
                  onSalaryChange={handleSalaryChange}
                  onFrequencyChange={setFrequency}
                  onFilingStatusChange={setFilingStatus}
                  onToggleAdvanced={() => setShowAdvanced(!showAdvanced)}
                />
              </article>
              <aside className="lg:col-span-2" aria-label="Calculation results">
                <ResultsPanel
                  taxResult={taxResult}
                  frequency={frequency}
                  currencySymbol={locationData.currencySymbol}
                  formatCurrency={formatCurrency}
                />
                <ForSaleCalculatorBlock />
              </aside>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <article>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                <strong>How {locationData.locationName} Income Tax &amp; Salary Deductions Work</strong>
              </h2>
              <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
                <p>{locationData.content.intro}</p>
                <div className="bg-teal-50 border-l-4 border-teal-500 p-4 rounded-r-lg">
                  <p><strong>Did you know?</strong> {locationData.content.fun_fact}</p>
                </div>
                <p>{locationData.content.hiring_stats}</p>
                
                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  <strong>Understanding Your {locationData.locationName} Paycheck Deductions</strong>
                </h3>
                <p>
                  When you receive your paycheck in {locationData.locationName}, several deductions are automatically taken out. 
                  These include <strong>federal income tax</strong>, <strong>state income tax</strong>, 
                  {locationData.city && <> <strong>local taxes</strong>,</>} <strong>Social Security</strong>, and <strong>Medicare</strong>. 
                  Our calculator breaks down each of these components so you can see exactly where your money goes.
                </p>
                
                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  <strong>{locationData.locationName} Federal &amp; State Tax Rates 2026</strong>
                </h3>
                <p>
                  The tax rates in {locationData.locationName} are determined by multiple factors including your income level, 
                  filing status, and location. Federal taxes apply to all US residents, while state and local taxes vary 
                  significantly by location. Use our calculator above to see your personalized tax breakdown.
                </p>
              </div>
            </article>
          </div>
        </section>

        {/* Tax Visualization */}
        <TaxVisualization
          taxResult={taxResult}
          locationData={locationData}
          formatCurrency={formatCurrency}
        />

        {/* Nearby Tools */}
        <NearbyTools
          locationName={locationData.locationName}
          nearbyCities={locationData.nearbyCities}
        />

        {/* FAQ Section */}
        <FAQSection
          locationName={locationData.locationName}
          salary={annualSalary}
          taxResult={taxResult}
          currencySymbol={locationData.currencySymbol}
          nearbyCities={locationData.nearbyCities}
        />

        {/* Blog Teaser */}
        <CityBlogTeaser
          cityName={locationData.city?.name || locationData.state?.name || locationData.country?.name || ''}
          stateName={locationData.state?.name || ''}
        />
      </main>

      {/* For Sale Footer Section */}
      <ForSaleFooterSection />

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="grid grid-cols-1 md:grid-cols-4 gap-8" aria-label="Footer navigation">
            <div>
              <h3 className="text-lg font-bold mb-4">Salary Calculator</h3>
              <p className="text-gray-400 text-sm">
                Accurate tax calculations for locations worldwide. Plan your finances with confidence.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Popular Locations</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/usa/nj/wood-ridge" className="hover:text-white transition-colors cursor-pointer" rel="nofollow">Wood-Ridge, NJ</a></li>
                <li><a href="/usa/ny/manhattan" className="hover:text-white transition-colors cursor-pointer" rel="nofollow">Manhattan, NY</a></li>
                <li><a href="/uk/london" className="hover:text-white transition-colors cursor-pointer" rel="nofollow">London, UK</a></li>
                <li><a href="/usa/ca/san-francisco" className="hover:text-white transition-colors cursor-pointer" rel="nofollow">San Francisco, CA</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/blog" className="hover:text-white transition-colors cursor-pointer">Tax Tips Blog</a></li>
                <li><a href="/about" className="hover:text-white transition-colors cursor-pointer">About Us</a></li>
                <li><a href="/contact" className="hover:text-white transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/privacy" className="hover:text-white transition-colors cursor-pointer">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-white transition-colors cursor-pointer">Terms of Service</a></li>
                <li><a href="/disclaimer" className="hover:text-white transition-colors cursor-pointer">Disclaimer</a></li>
                <li><a href="/sitemap" className="hover:text-white transition-colors cursor-pointer">Sitemap</a></li>
              </ul>
            </div>
          </nav>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>© 2026 Salary Tax Calculator. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Powered by Readdy</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CalculatorPage;